import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCustomers,
  getSocieties,
  createCustomer,
  updateCustomer,
  addCustomerCredits,
  getCreditRequests,
  approveCreditRequest,
  getCustomerCreditTransactions,
  getCustomerSocieties,
  getStatPriceParameters,
  getCustomerStatPrices,
  upsertCustomerStatPrice,
  deleteCustomerStatPrice,
} from '../services/api';
import type {
  CustomerRecord,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CustomerCreditRequest,
  CustomerCreditTransaction,
  CustomerStatPrice,
} from '../services/api';
import './AdminCustomers.css';

const emptyCustomerForm: CreateCustomerRequest & { id?: number } = {
  customer_name: '',
  business_name: '',
  email: '',
  password: '',
  address: '',
  location: '',
  city: '',
  state: '',
  pin_code: '',
  credits: 0,
  society_ids: [],
};

const AdminCustomers: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [allSocieties, setAllSocieties] = useState<Array<{ id: number; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerRecord | null>(null);
  const [formData, setFormData] = useState<CreateCustomerRequest & { id?: number }>(emptyCustomerForm);
  const [formError, setFormError] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [addCreditsCustomer, setAddCreditsCustomer] = useState<CustomerRecord | null>(null);
  const [addCreditsAmount, setAddCreditsAmount] = useState('');
  const [addCreditsNotes, setAddCreditsNotes] = useState('');
  const [addCreditsSaving, setAddCreditsSaving] = useState(false);
  const [creditRequests, setCreditRequests] = useState<CustomerCreditRequest[]>([]);
  const [showCreditRequests, setShowCreditRequests] = useState(false);
  const [creditRequestsLoading, setCreditRequestsLoading] = useState(false);
  const [transactionsCustomer, setTransactionsCustomer] = useState<CustomerRecord | null>(null);
  const [transactions, setTransactions] = useState<CustomerCreditTransaction[]>([]);
  const [statPricingCustomer, setStatPricingCustomer] = useState<CustomerRecord | null>(null);
  const [statPrices, setStatPrices] = useState<CustomerStatPrice[]>([]);
  const [statPriceParams, setStatPriceParams] = useState<string[]>([]);
  const [statPriceInputs, setStatPriceInputs] = useState<Record<string, string>>({});
  const [statPricingSaving, setStatPricingSaving] = useState(false);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const [custRes, socRes] = await Promise.all([
        getCustomers(),
        getSocieties(),
      ]);
      setCustomers(custRes.data);
      setAllSocieties(socRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    getCreditRequests().then((r) => setCreditRequests(r.data)).catch(() => {});
  }, [showCreditRequests, customers.length]);

  const openCreate = async () => {
    setEditingCustomer(null);
    setFormData({ ...emptyCustomerForm, society_ids: [] });
    setFormError('');
    setShowForm(true);
    // Refetch societies when opening form so the list is available (e.g. if initial load failed or was empty)
    try {
      const socRes = await getSocieties();
      setAllSocieties(socRes.data);
    } catch (e) {
      console.error('Failed to load societies:', e);
    }
  };

  const openEdit = (c: CustomerRecord) => {
    setEditingCustomer(c);
    setFormData({
      customer_name: c.customer_name,
      business_name: c.business_name ?? '',
      email: c.email,
      password: '',
      address: c.address ?? '',
      location: c.location ?? '',
      city: c.city ?? '',
      state: c.state ?? '',
      pin_code: c.pin_code ?? '',
      credits: c.credits,
      society_ids: [],
    });
    setFormError('');
    setShowForm(true);
    getCustomerSocieties(c.id).then((r) => setFormData((prev) => ({ ...prev, society_ids: r.data.map((s) => s.id) })));
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingCustomer(null);
    setFormData(emptyCustomerForm);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!formData.customer_name.trim() || !formData.email.trim()) {
      setFormError('Customer name and email are required.');
      return;
    }
    if (!editingCustomer && !formData.password) {
      setFormError('Password is required for new customers.');
      return;
    }
    setFormSaving(true);
    try {
      if (editingCustomer) {
        const payload: UpdateCustomerRequest = {
          customer_name: formData.customer_name,
          business_name: formData.business_name || undefined,
          email: formData.email,
          address: formData.address || undefined,
          location: formData.location || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          pin_code: formData.pin_code || undefined,
          credits: formData.credits,
          society_ids: formData.society_ids,
        };
        if (formData.password) payload.password = formData.password;
        await updateCustomer(editingCustomer.id, payload);
      } else {
        await createCustomer({
          ...formData,
          password: formData.password,
          society_ids: formData.society_ids,
        });
      }
      closeForm();
      loadCustomers();
    } catch (err: any) {
      setFormError(err.message || 'Failed to save customer');
    } finally {
      setFormSaving(false);
    }
  };

  const openAddCredits = (c: CustomerRecord) => {
    setAddCreditsCustomer(c);
    setAddCreditsAmount('');
    setAddCreditsNotes('');
  };

  const handleAddCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addCreditsCustomer) return;
    const amount = parseFloat(addCreditsAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Enter a valid positive amount.');
      return;
    }
    setAddCreditsSaving(true);
    try {
      await addCustomerCredits(addCreditsCustomer.id, amount, addCreditsNotes || undefined);
      setAddCreditsCustomer(null);
      loadCustomers();
    } catch (err: any) {
      alert(err.message || 'Failed to add credits');
    } finally {
      setAddCreditsSaving(false);
    }
  };

  const loadCreditRequests = async () => {
    setCreditRequestsLoading(true);
    try {
      const res = await getCreditRequests();
      setCreditRequests(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setCreditRequestsLoading(false);
    }
  };

  const openCreditRequests = () => {
    setShowCreditRequests(true);
    loadCreditRequests();
  };

  const handleApproveRequest = async (id: number, approve: boolean) => {
    try {
      await approveCreditRequest(id, approve);
      loadCreditRequests();
      loadCustomers();
    } catch (err: any) {
      alert(err.message || 'Action failed');
    }
  };

  const openTransactions = async (c: CustomerRecord) => {
    setTransactionsCustomer(c);
    try {
      const res = await getCustomerCreditTransactions(c.id);
      setTransactions(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleSociety = (id: number) => {
    setFormData((prev) => {
      const ids = prev.society_ids || [];
      const next = ids.includes(id) ? ids.filter((s) => s !== id) : [...ids, id];
      return { ...prev, society_ids: next };
    });
  };

  const openStatPricing = async (c: CustomerRecord) => {
    setStatPricingCustomer(c);
    setStatPrices([]);
    setStatPriceParams([]);
    setStatPriceInputs({});
    try {
      const [paramsRes, pricesRes] = await Promise.all([
        getStatPriceParameters(),
        getCustomerStatPrices(c.id),
      ]);
      setStatPriceParams(paramsRes.data || []);
      setStatPrices(pricesRes.data || []);
      const inputs: Record<string, string> = {};
      (pricesRes.data || []).forEach((p) => { inputs[p.stat_parameter] = String(p.price_per_unit); });
      setStatPriceInputs(inputs);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveStatPrice = async (param: string, value: string) => {
    if (!statPricingCustomer) return;
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    setStatPricingSaving(true);
    try {
      await upsertCustomerStatPrice(statPricingCustomer.id, param, num);
      const list = await getCustomerStatPrices(statPricingCustomer.id);
      setStatPrices(list.data);
      setStatPriceInputs((prev) => ({ ...prev, [param]: value }));
    } catch (e) {
      console.error(e);
    } finally {
      setStatPricingSaving(false);
    }
  };

  const handleDeleteStatPrice = async (param: string) => {
    if (!statPricingCustomer) return;
    setStatPricingSaving(true);
    try {
      await deleteCustomerStatPrice(statPricingCustomer.id, param);
      setStatPrices((prev) => prev.filter((p) => p.stat_parameter !== param));
      setStatPriceInputs((prev) => { const next = { ...prev }; delete next[param]; return next; });
    } catch (e) {
      console.error(e);
    } finally {
      setStatPricingSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-customers-loading">Loading customers...</div>;
  }

  return (
    <div className="admin-customers">
      <div className="admin-customers-header">
        <h2>Customers</h2>
        <div className="admin-customers-actions">
          <button type="button" className="btn-secondary" onClick={openCreditRequests}>
            Credit Requests
            {creditRequests.filter((r) => r.status === 'pending').length > 0 && (
              <span className="badge-pending">{creditRequests.filter((r) => r.status === 'pending').length}</span>
            )}
          </button>
          <button type="button" className="btn-primary" onClick={openCreate}>
            + Add Customer
          </button>
        </div>
      </div>

      <div className="admin-customers-table-wrap">
        <table className="admin-customers-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Customer Name</th>
              <th>Business</th>
              <th>Email</th>
              <th>City</th>
              <th>Credits</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty-state">No customers. Add one to get started.</td>
              </tr>
            ) : (
              customers.map((c) => (
                <tr
                  key={c.id}
                  className="customer-row-clickable"
                  onClick={() => navigate(`/admin/customers/${c.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/admin/customers/${c.id}`);
                    }
                  }}
                >
                  <td>{c.id}</td>
                  <td>{c.customer_name}</td>
                  <td>{c.business_name || '-'}</td>
                  <td>{c.email}</td>
                  <td>{c.city || '-'}</td>
                  <td>{c.credits}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button type="button" className="edit-btn" onClick={() => openEdit(c)}>Edit</button>
                      <button type="button" className="credits-btn" onClick={() => openAddCredits(c)}>Add Credits</button>
                      <button type="button" className="pricing-btn" onClick={() => openStatPricing(c)}>Stat pricing</button>
                      <button type="button" className="history-btn" onClick={() => openTransactions(c)}>History</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit modal */}
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content admin-customer-form" onClick={(e) => e.stopPropagation()}>
            <h3>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h3>
            {formError && <div className="form-error">{formError}</div>}
            <form onSubmit={handleFormSubmit}>
              <div className="form-row">
                <label>Customer Name *</label>
                <input
                  value={formData.customer_name}
                  onChange={(e) => setFormData((p) => ({ ...p, customer_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-row">
                <label>Business Name</label>
                <input
                  value={formData.business_name || ''}
                  onChange={(e) => setFormData((p) => ({ ...p, business_name: e.target.value }))}
                />
              </div>
              <div className="form-row">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                  required
                  readOnly={!!editingCustomer}
                />
                {editingCustomer && <small>Email cannot be changed</small>}
              </div>
              <div className="form-row">
                <label>Password {editingCustomer ? '(leave blank to keep)' : '*'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                  placeholder={editingCustomer ? 'Leave blank to keep current' : ''}
                />
              </div>
              <div className="form-row">
                <label>Address</label>
                <input
                  value={formData.address || ''}
                  onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))}
                />
              </div>
              <div className="form-row">
                <label>Location</label>
                <input
                  value={formData.location || ''}
                  onChange={(e) => setFormData((p) => ({ ...p, location: e.target.value }))}
                />
              </div>
              <div className="form-row two-cols">
                <div>
                  <label>City</label>
                  <input
                    value={formData.city || ''}
                    onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))}
                  />
                </div>
                <div>
                  <label>State</label>
                  <input
                    value={formData.state || ''}
                    onChange={(e) => setFormData((p) => ({ ...p, state: e.target.value }))}
                  />
                </div>
              </div>
              <div className="form-row">
                <label>Pin Code</label>
                <input
                  value={formData.pin_code || ''}
                  onChange={(e) => setFormData((p) => ({ ...p, pin_code: e.target.value }))}
                />
              </div>
              {editingCustomer && (
                <div className="form-row">
                  <label>Credits</label>
                  <input
                    type="number"
                    min={0}
                    value={formData.credits}
                    onChange={(e) => setFormData((p) => ({ ...p, credits: parseInt(e.target.value, 10) || 0 }))}
                  />
                </div>
              )}
              <div className="form-row societies-row">
                <label>Societies (enabled for this customer)</label>
                <p className="form-hint">Select one or more societies where this customer can run promotions.</p>
                <div className="society-checkboxes">
                  {allSocieties.length === 0 ? (
                    <p className="no-societies-msg">No societies available. Add societies in the Societies section first, or ensure you are logged in as admin.</p>
                  ) : (
                    allSocieties.map((s) => (
                      <label key={s.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={(formData.society_ids || []).includes(s.id)}
                          onChange={() => toggleSociety(s.id)}
                        />
                        {s.name}
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={formSaving}>
                  {formSaving ? 'Saving...' : editingCustomer ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add credits modal */}
      {addCreditsCustomer && (
        <div className="modal-overlay" onClick={() => setAddCreditsCustomer(null)}>
          <div className="modal-content add-credits-form" onClick={(e) => e.stopPropagation()}>
            <h3>Add Credits — {addCreditsCustomer.customer_name}</h3>
            <p>Current balance: {addCreditsCustomer.credits}</p>
            <form onSubmit={handleAddCredits}>
              <div className="form-row">
                <label>Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={addCreditsAmount}
                  onChange={(e) => setAddCreditsAmount(e.target.value)}
                  required
                />
              </div>
              <div className="form-row">
                <label>Notes (optional)</label>
                <input
                  value={addCreditsNotes}
                  onChange={(e) => setAddCreditsNotes(e.target.value)}
                  placeholder="e.g. Top-up"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setAddCreditsCustomer(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={addCreditsSaving}>
                  {addCreditsSaving ? 'Adding...' : 'Add Credits'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credit requests modal */}
      {showCreditRequests && (
        <div className="modal-overlay" onClick={() => setShowCreditRequests(false)}>
          <div className="modal-content credit-requests-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Credit Requests</h3>
            {creditRequestsLoading ? (
              <p>Loading...</p>
            ) : (
              <div className="credit-requests-list">
                {creditRequests.filter((r) => r.status === 'pending').length === 0 ? (
                  <p>No pending requests.</p>
                ) : (
                  creditRequests
                    .filter((r) => r.status === 'pending')
                    .map((r) => (
                      <div key={r.id} className="credit-request-item">
                        <span>Request #{r.id} — Customer ID {r.customer_id} — Amount: {r.amount}</span>
                        <div>
                          <button type="button" className="approve-btn" onClick={() => handleApproveRequest(r.id, true)}>Approve</button>
                          <button type="button" className="reject-btn" onClick={() => handleApproveRequest(r.id, false)}>Reject</button>
                        </div>
                      </div>
                    ))
                )}
              </div>
            )}
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowCreditRequests(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Stat pricing modal */}
      {statPricingCustomer && (
        <div className="modal-overlay" onClick={() => setStatPricingCustomer(null)}>
          <div className="modal-content stat-pricing-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Stat pricing — {statPricingCustomer.customer_name}</h3>
            <p className="form-hint">Configure price per unit for each promotion statistic parameter. Same price = deductions combine into one entry.</p>
            <div className="stat-pricing-list">
              {statPriceParams.map((param) => {
                const existing = statPrices.find((p) => p.stat_parameter === param);
                const inputVal = statPriceInputs[param] ?? (existing ? String(existing.price_per_unit) : '');
                return (
                  <div key={param} className="stat-pricing-row">
                    <span className="stat-param-label">{param}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={inputVal}
                      onChange={(e) => setStatPriceInputs((p) => ({ ...p, [param]: e.target.value }))}
                      placeholder="0"
                    />
                    <button
                      type="button"
                      className="btn-primary btn-small"
                      onClick={() => handleSaveStatPrice(param, inputVal)}
                      disabled={statPricingSaving || inputVal === '' || parseFloat(inputVal) < 0}
                    >
                      Save
                    </button>
                    {existing && (
                      <button
                        type="button"
                        className="btn-secondary btn-small"
                        onClick={() => handleDeleteStatPrice(param)}
                        disabled={statPricingSaving}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {statPriceParams.length === 0 && <p>Loading parameters...</p>}
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setStatPricingCustomer(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction history modal */}
      {transactionsCustomer && (
        <div className="modal-overlay" onClick={() => setTransactionsCustomer(null)}>
          <div className="modal-content transactions-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Credit History — {transactionsCustomer.customer_name}</h3>
            <div className="transactions-list">
              {transactions.length === 0 ? (
                <p>No transactions yet.</p>
              ) : (
                <table className="mini-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Type</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => (
                      <tr key={t.id}>
                        <td>{new Date(t.created_at).toLocaleString()}</td>
                        <td>{t.amount}</td>
                        <td>{t.type}</td>
                        <td>{t.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setTransactionsCustomer(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
